import OrderCargoOrderDetails, {
  OrderCargoOrderDetailsCargoOrderCargoSourceExternal,
  OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
  OrderCargoOrderDetailsCargoOrderRideSeq,
  OrderCargoOrderDetailsCargoOrderRideSeqNode,
  OrderCargoOrderDetailsCargoOrderRideSeqNodeSet,
  OrderCargoOrderDetailsPassengerItem,
  OrderCargoOrderDetailsPassengerItemPassengerExternal,
} from "../../../../../../common/services/order/cargo-order/details/order-cargo-order-details";
import OrderUpdateParams, {
  OrderUpdateParamsExternalCargoItemSource,
  OrderUpdateParamsInternalCargoItemSource,
  OrderUpdateSeqGroupNodeRequest,
  OrderUpdateSeqNodeRequest,
} from "../../../../../../common/services/order/update/order-update-params";
import SourceType from "../../../../../../common/types/source-type";

const matchPassengerFromList = (
  passengers: OrderCargoOrderDetailsPassengerItem[],
  subject: OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
): OrderCargoOrderDetailsPassengerItem => {
  return passengers.find((passenger) => passenger.uuid === subject.uuid)!;
};

const createSeqNode = (
  node: OrderCargoOrderDetailsCargoOrderRideSeqNode,
  passengers: OrderCargoOrderDetailsPassengerItem[],
) => {
  const seqNode: OrderUpdateSeqNodeRequest = {
    displayName: node.displayName,
    haltingTime: node.haltingTime,
    lat: node.lat,
    lon: node.lon,
    time: null,
    meta: {
      id: node.meta.uuid,
      cargoEnter: {
        subjects: node.meta.cargoEnter.subjects.map((subject) => {
          return {
            id: matchPassengerFromList(passengers, subject).passenger.uuid,
          };
        }),
      },
      cargoExit: {
        subjects: node.meta.cargoExit.subjects.map((subject) => {
          return {
            id: matchPassengerFromList(passengers, subject).passenger.uuid,
          };
        }),
      },
    },
  };

  return seqNode;
};

const createSeq = (
  seq: OrderCargoOrderDetailsCargoOrderRideSeq[],
  passengers: OrderCargoOrderDetailsPassengerItem[],
  plannedStartTime: Date,
): (OrderUpdateSeqNodeRequest | OrderUpdateSeqGroupNodeRequest)[] => {
  const patchSequence: (
    | OrderUpdateSeqNodeRequest
    | OrderUpdateSeqGroupNodeRequest
  )[] = [];

  let firstNode = true;

  seq.forEach((seqNode) => {
    const nodeSet = seqNode as OrderCargoOrderDetailsCargoOrderRideSeqNodeSet;
    if (nodeSet.seq) {
      const seqNodeGroup: OrderUpdateSeqGroupNodeRequest = {
        seq: nodeSet.seq.map((node) => createSeqNode(node, passengers)),
        meta: {
          id: nodeSet.meta.uuid,
        },
        sequenceOrder: "ANY",
      };

      patchSequence.push(seqNodeGroup);
    } else {
      const node = seqNode as OrderCargoOrderDetailsCargoOrderRideSeqNode;

      const patchNode: OrderUpdateSeqNodeRequest = createSeqNode(
        node,
        passengers,
      );

      if (firstNode) {
        firstNode = false;
        patchNode.time = plannedStartTime;
      }

      patchSequence.push(patchNode);
    }
  });

  return patchSequence;
};

const createRequest = (
  details: OrderCargoOrderDetails,
  plannedStartTime: Date,
): OrderUpdateParams => {
  const rv2: Record<
    string,
    | OrderUpdateParamsInternalCargoItemSource
    | OrderUpdateParamsExternalCargoItemSource
  > = {};

  const internalPassengers: OrderCargoOrderDetailsPassengerItem[] = [];

  details.passengers.forEach((passenger) => {
    const externalId =
      details.cargoOrder.cargoItemsSourceList.find((item) => {
        item.cargoSubjectUuid == passenger.uuid;
      })?.cargoItemSource.externalId ?? "";

    const internalPassenger: OrderCargoOrderDetailsPassengerItem = {
      uuid: passenger.uuid,
      passenger: {
        type: SourceType.INTERNAL,
        externalId: externalId,
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        uuid: passenger.uuid,
      },
    };

    internalPassengers.push(internalPassenger);

    const internal: OrderUpdateParamsInternalCargoItemSource = {
      sourceType: "INTERNAL",
      externalId: externalId,
      passengerId: internalPassenger.passenger.uuid,
    };

    rv2[internalPassenger.passenger.uuid] = internal;
  });

  const externalPassengers: OrderCargoOrderDetailsPassengerItem[] = [];

  const externalSubjects = details.cargoOrder.cargoItemsSourceList.filter(
    (subject) => subject.cargoItemSource.sourceType === SourceType.EXTERNAL,
  );

  if (externalSubjects.length) {
    externalSubjects.forEach((subject) => {
      const subjectId = subject.cargoSubjectUuid;
      const subjectData =
        subject.cargoItemSource as OrderCargoOrderDetailsCargoOrderCargoSourceExternal;

      const externalPassenger: OrderCargoOrderDetailsPassengerItem = {
        uuid: subjectId,
        passenger: {
          type: SourceType.EXTERNAL,
          dispatchName: subjectData.dispatchName ?? "",
          externalId: subjectData.externalId ?? "",
          firstName: subjectData.firstName,
          lastName: subjectData.lastName,
          mobile: subjectData.phoneNo,
          uuid: subjectId,
        },
      };

      externalPassengers.push(externalPassenger);

      const listItemPassenger =
        externalPassenger.passenger as OrderCargoOrderDetailsPassengerItemPassengerExternal;

      const external: OrderUpdateParamsExternalCargoItemSource = {
        sourceType: "EXTERNAL",
        externalId: listItemPassenger.externalId ?? "",
        dispatchName: listItemPassenger.dispatchName ?? "",
        firstName: listItemPassenger.firstName,
        lastName: listItemPassenger.lastName,
        phoneNo: listItemPassenger.mobile,
      };

      rv2[subjectId] = external;
    });
  }

  const passengers: OrderCargoOrderDetailsPassengerItem[] = [
    ...internalPassengers,
    ...externalPassengers,
  ];

  return {
    forceAllowToll: details.forceAllowToll,
    rv1: {
      meta: {
        id: details.cargoOrder.ride.meta.uuid,
      },
      sequenceOrder: "STRICT",
      seq: createSeq(details.cargoOrder.ride.seq, passengers, plannedStartTime),
    },
    rv2,
    orderId: details.id,
  };
};

const orderDetailsManageDriverAssignmentCollisionEditRequestFactory = {
  createRequest,
};

export default orderDetailsManageDriverAssignmentCollisionEditRequestFactory;
